import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./ListingPage.scss";
import CommonButton from "../../components/CommonButton/CommonButton";
import CommonText from "../../components/CommonText/CommonText";
import CommonLoader from "../../components/CommonLoader/CommonLoader";
import CommonModal from "../../components/CommonModal/CommonModal";
import CommonSelect from "../../components/CommonSelect/CommonSelect";
import apiClient from "../../api/apiClient";
import apiEndpoints from "../../api/apiEndpoints";
import axios from "axios";

type Option = {
  id: number;
  type?: string;
  sequence: number;
  name: string;
  btnText: string;
  nextSequence?: number;
};

type Question = {
  id: number;
  sequence: number;
  type: string;
  text: string;
  eeva_image?: string;
  btnTxt?: string;
  validationType: string;
  nextSequence?: any;
  options?: Option[];
};

interface ApiResponse<T> {
  questions: T[];
}

interface Country {
  id: number;
  countryName: string;
  countryCode: string;
}

interface ListingPageProps {
  clientId: string | number;
  clientName: string;
  logoURL: string | null;
  sessionId: string | number;
  countries: Country[];
  onDataFetched: () => void;
}

interface Message {
  text: string;
  sender: "user" | "eeva";
}

interface EevaResponse {
  aiResponse: string;
  message: string;
}

const ListingPage: React.FC<ListingPageProps> = ({
  clientId,
  clientName,
  logoURL,
  sessionId,
  countries,
  onDataFetched,
}) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [showErrorModal, toggleErrorModal] = useState<boolean>(false);
  const [userData, setUserData] = useState<Record<string | number, any>>({});
  const [userDataForChat, setUserDataForChat] = useState<
    Record<string | number, any>
  >({});
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState<
    string | number
  >("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [isButtonClicked, setIsButtonClicked] = useState<boolean>(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isChatMode, setIsChatMode] = useState<boolean>(false);
  const [userMessage, setUserMessage] = useState<string>("");
  const [chatLoading, setChatLoading] = useState<boolean>(false);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);

  const fetchQuestions = async () => {
    if (clientId !== null) {
      try {
        const response = await apiClient.get<ApiResponse<Question>>(
          apiEndpoints.getQuestions(),
          {
            params: { clientId },
          }
        );
        if (response && response.data) {
          setQuestions(response.data.questions);
          setCurrentStep(response.data.questions[0].sequence);
        }
      } catch (error) {
        console.error("Error fetching questions:", error);
      } finally {
        setTimeout(() => {
          onDataFetched();
        }, 500);
      }
    } else {
      navigate("/");
    }
  };

  useEffect(() => {
    if (clientId !== null) {
      fetchQuestions();
      setIsChatMode(false);
    }
  }, [clientId, onDataFetched]);

  const validateField = (
    value: string,
    validationType: string
  ): { isValid: boolean; errorMessage: string } => {
    switch (validationType) {
      case "not empty":
        return {
          isValid: value.trim() !== "",
          errorMessage: "Please enter your input.",
        };
      case "email":
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return {
          isValid: emailRegex.test(value),
          errorMessage: "Please enter a valid email address.",
        };
      case "phonenumber":
        const phoneRegex = /^[0-9]{10}$/;
        return {
          isValid: phoneRegex.test(value),
          errorMessage: "Please enter a valid phone number (10 digits).",
        };
      case "no validation":
        return { isValid: true, errorMessage: "" };
      default:
        return { isValid: true, errorMessage: "" };
    }
  };

  const handleFieldChange = (
    questionId: number | string,
    question: string,
    value: string | number
  ) => {
    if (
      typeof questionId === "number" &&
      typeof question === "string" &&
      typeof value === "string" &&
      value.trim() !== ""
    ) {
      if (questionId.toString().endsWith("-countryCode")) {
        setUserData((prevData) => ({
          ...prevData,
          [`${questionId.toString()}`]: value,
        }));
        setUserDataForChat((prevData) => ({
          ...prevData,
          question: value,
        }));
      } else {
        setUserData((prevData) => ({
          ...prevData,
          [questionId.toString()]: value,
        }));
        setUserDataForChat((prevData) => ({
          ...prevData,
          [question]: value,
        }));
      }
    }
  };

  const handleCountryChange = (value: string | number) => {
    setSelectedCountryCode(value);
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(e.target.value);
  };

  const handlePhoneNumberValidation = (nextQuestion: Question | undefined) => {
    const currentQuestion = questions.find(
      (step) => step.sequence === currentStep
    );

    if (!currentQuestion || !selectedCountryCode || !phoneNumber) return;

    const validationResult = validateField(phoneNumber, "phonenumber");

    if (!validationResult.isValid) {
      return;
    }
    setIsButtonClicked(false);
    const fullPhoneNumber = `${selectedCountryCode} ${phoneNumber}`;

    setUserData((prevData) => ({
      ...prevData,
      [currentQuestion.id.toString()]: fullPhoneNumber,
    }));

    setUserDataForChat((prevData) => ({
      ...prevData,
      [currentQuestion.text]: fullPhoneNumber,
    }));

    const nextStep = currentQuestion.nextSequence;
    if (nextQuestion?.nextSequence == "null") {
      postUserResponse(userData, nextStep);
    } else {
      setCurrentStep(nextStep);
    }
  };

  const handleUserMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserMessage(e.target.value);
  };

  const handleTalkToEevaClick = () => {
    setIsChatMode(true);
    const welcomeMessage: Message = {
      text: "Hi! How can I assist you today?",
      sender: "eeva",
    };
    setMessages((prevMessages) => [...prevMessages, welcomeMessage]);
  };

  const chatWithAI = async () => {
    if (!userMessage.trim()) return;

    setUserMessage("");
    const newMessage: Message = { text: userMessage, sender: "user" };
    setMessages((prevMessages) => [...prevMessages, newMessage]);

    setChatLoading(true);
    try {
      const userReq = {
        clientId: clientId,
        sessionId: sessionId,
        userText: userMessage,
      };
      const postResponse = await apiClient.post<EevaResponse>(
        apiEndpoints.getAIResponse(userReq),
        userReq
      );
      const evaMessage: Message = {
        text: postResponse.data.aiResponse,
        sender: "eeva",
      };

      setMessages((prevMessages) => [...prevMessages, evaMessage]);
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setChatLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && userMessage.trim() !== "") {
      chatWithAI();
      setUserMessage("");
    }
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleButtonClick = (
    nextStep: number,
    selectedOptionValue?: string
  ) => {
    const currentQuestion = questions.find(
      (step) => step.sequence === currentStep
    );
    const nextQuestion = questions.find((step) => step.sequence === nextStep);

    if (!currentQuestion) return;

    if (currentQuestion.validationType === "phonenumber") {
      setIsButtonClicked(true);
      handlePhoneNumberValidation(nextQuestion);
    } else {
      if (selectedOptionValue !== undefined) {
        setUserData((prevData) => ({
          ...prevData,
          [currentQuestion.id.toString()]: selectedOptionValue,
        }));
        setUserDataForChat((prevData) => ({
          ...prevData,
          [currentQuestion.text]: selectedOptionValue,
        }));
      }

      setIsButtonClicked(true);

      const validationResult = validateField(
        userData[currentQuestion.id] || "",
        currentQuestion.validationType
      );

      if (validationResult.isValid) {
        setIsButtonClicked(false);

        if (nextQuestion?.nextSequence == "null") {
          postUserResponse(userData, nextStep);
        } else {
          setCurrentStep(nextStep);
        }
      } else {
        console.log("Invalid input, cannot proceed.");
      }
    }
  };

  const updatePlaceholders = (inputText: string) => {
    const updatedText = inputText
      .replace(/<response(\d+)>/g, (_, key) => {
        const questionId = questions.find(
          (question) => question.sequence == parseInt(key)
        )?.id;
        return questionId && userData[questionId] ? userData[questionId] : "";
      })
      .replace(/<clientName>/g, clientName);
    return updatedText;
  };

  const currentQuestion = questions.find(
    (step) => step.sequence === currentStep
  );

  if (!currentQuestion) return null;

  const renderMessage = () => {
    const message = updatePlaceholders(currentQuestion.text);
    return (
      <div className="w-75">
        <div className="eeva-container vertical-center">
          <img
            src={
              currentQuestion.eeva_image &&
              currentQuestion.eeva_image !== "null"
                ? currentQuestion.eeva_image
                : "/assets/images/eeva/eeva_default.gif"
            }
            alt="EveA"
          />
        </div>
        <div dangerouslySetInnerHTML={{ __html: message }} />
        <CommonButton
          btnText={currentQuestion.btnTxt}
          onClick={() => {
            handleButtonClick(currentQuestion.nextSequence!);
          }}
          className="fill-btn"
          color="#FFF"
          height="40px"
          backgroundColor="none"
          border="none"
          borderRadius="24px"
        />
      </div>
    );
  };

  const renderSelect = () => {
    const message = updatePlaceholders(currentQuestion.text);
    return (
      <div>
        <div className="eeva-container vertical-center">
          <img
            src={
              currentQuestion.eeva_image &&
              currentQuestion.eeva_image !== "null"
                ? currentQuestion.eeva_image
                : "/assets/images/eeva/eeva_answer.gif"
            }
            alt="EveA"
          />
        </div>
        <div dangerouslySetInnerHTML={{ __html: message }} />
        {currentQuestion.options?.map((option) => (
          <CommonButton
            key={option.id}
            btnText={option.btnText}
            onClick={() => handleButtonClick(option.nextSequence!, option.name)}
            className="fill-btn options-btn m-2"
            color="#FFF"
            height="40px"
            backgroundColor="none"
            border="none"
            borderRadius="24px"
          />
        ))}
      </div>
    );
  };

  const renderForm = () => {
    const message = updatePlaceholders(currentQuestion.text);
    const currentQuestionId = currentQuestion.id;
    const currentQuestionAnswer = userData[currentQuestionId] || "";
    const validationResult = validateField(
      currentQuestionAnswer,
      currentQuestion.validationType
    );
    const isValid = validationResult.isValid;
    const errorMessage = validationResult.errorMessage;

    return (
      <div>
        <div className="eeva-container vertical-center">
          <img
            src={
              currentQuestion.eeva_image &&
              currentQuestion.eeva_image !== "null"
                ? currentQuestion.eeva_image
                : "/assets/images/eeva/eeva_answer.gif"
            }
            alt="EveA"
          />
        </div>
        <div dangerouslySetInnerHTML={{ __html: message }} />

        <div className="d-flex gap-2 mt-3 mb-3">
          {currentQuestion.options?.map((option, index) => (
            <div key={index} className="w-100">
              {option.type === "text" &&
                currentQuestion.validationType !== "phonenumber" && (
                  <CommonText
                    value={currentQuestionAnswer}
                    onChange={(e) =>
                      handleFieldChange(
                        currentQuestionId,
                        message,
                        e.target.value
                      )
                    }
                    placeholder={option.name}
                    className="w-100"
                    backgroundColor="#FFF"
                    border="none"
                    borderBottom="1px solid #E6E6F0"
                    borderTop="none"
                  />
                )}

              {currentQuestion.validationType === "phonenumber" && (
                <div className="d-flex gap-2">
                  <CommonSelect
                    options={countries}
                    className="w-50 common-size-14"
                    selected={selectedCountryCode}
                    onSelect={handleCountryChange}
                    placeholder="Country Code"
                    nameKey="countryCode"
                  />
                  <CommonText
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    placeholder="Enter phone number"
                    className="w-75"
                    backgroundColor="#FFF"
                    border="none"
                    borderBottom="1px solid #E6E6F0"
                    borderTop="none"
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {isButtonClicked && !isValid && (
          <div className="common-error-message">{errorMessage}</div>
        )}

        <CommonButton
          btnText={currentQuestion.btnTxt}
          onClick={() => handleButtonClick(currentQuestion.nextSequence!)}
          className="fill-btn"
          color="#FFF"
          height="40px"
          backgroundColor="none"
          border="none"
          borderRadius="24px"
        />
      </div>
    );
  };

  const renderConclusion = () => {
    const message = updatePlaceholders(currentQuestion.text);

    return (
      <>
        {isChatMode ? (
          <>
            <>
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`chat-message ${
                    message.sender === "user" ? "user-message" : "eeva-message"
                  }`}>
                  <span
                    dangerouslySetInnerHTML={{ __html: message.text }}></span>
                </div>
              ))}
              {chatLoading && <div className="spinner-grow"></div>}
            </>
          </>
        ) : (
          <>
            <div className="eeva-container vertical-center">
              <img
                src={
                  currentQuestion.eeva_image &&
                  currentQuestion.eeva_image !== "null"
                    ? currentQuestion.eeva_image
                    : "/assets/images/eeva/eeva_thumbsup.gif"
                }
                alt="EveA"
              />
            </div>
            <div dangerouslySetInnerHTML={{ __html: message }} />
            <CommonButton
              btnText="Talk to Eeva"
              onClick={handleTalkToEevaClick}
              className="talk-to-eeva-btn w-25 mt-2"
              color="#FFF"
              height="40px"
              border="none"
              borderRadius="24px"
            />
          </>
        )}
      </>
    );
  };

  const postUserResponse = async (
    response: Record<string | number, any>,
    nextStep: number
  ) => {
    setLoading(true);
    try {
      const responseObj = {
        clientId: clientId,
        sessionId: sessionId,
        userData: response,
      };
      const postResponse = await apiClient.post(
        apiEndpoints.postUserResponse(responseObj),
        responseObj
      );
      if (postResponse.status === 200) {
        setCurrentStep(nextStep);
      } else {
        toggleErrorModal(true);
      }
    } catch (err) {
      toggleErrorModal(true);
      console.error("Error submitting response:", err);
    } finally {
      setLoading(false);
    }
  };

  const renderCurrentStep = () => {
    switch (currentQuestion.type) {
      case "message":
        return renderMessage();
      case "freetext":
        return renderForm();
      case "options":
        return renderSelect();
      case "conclusion":
        return renderConclusion();
      default:
        return null;
    }
  };

  const closeErrorMessageModal = () => {
    toggleErrorModal(false);
  };

  return (
    <>
      <div className="content-container">
        <div className="banner-container"></div>
        <div className="d-flex flex-column align-center-mobile margin-bottom-100">
          <img
            src={
              logoURL && logoURL !== "null"
                ? logoURL
                : "/assets/images/void_logo_white.png"
            }
            className="banner-void-logo"
            alt="eeva-logo"
          />
          {loading ? (
            <CommonLoader />
          ) : (
            <>
              {isChatMode ? (
                <div className="eeva-container vertical-center chat-section-enabled">
                  <img
                    src={
                      currentQuestion.eeva_image &&
                      currentQuestion.eeva_image !== "null"
                        ? currentQuestion.eeva_image
                        : "/assets/images/eeva/eeva_default.gif"
                    }
                    alt="EveA"
                  />
                </div>
              ) : null}

              <div className={`chat-container ${isChatMode ? "mt-0" : ""}`}>
                <div className="chat-messages" ref={chatContainerRef}>
                  <div className="form-page active-page">
                    {renderCurrentStep()}
                  </div>
                </div>
                {isChatMode ? (
                  <div className="chat-input">
                    <CommonText
                      value={userMessage}
                      onChange={handleUserMessageChange}
                      onKeyDown={handleKeyPress}
                      placeholder="Enter your message"
                      className="w-75"
                      backgroundColor="#FFF"
                      border="none"
                      borderBottom="1px solid #E6E6F0"
                      borderTop="none"
                    />
                    <CommonButton
                      btnText="Send"
                      onClick={chatWithAI}
                      className="fill-btn"
                      color="#FFF"
                      height="40px"
                      backgroundColor="#00c851"
                      border="none"
                      borderRadius="24px"
                      type="submit"
                      disabled={chatLoading}
                    />
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
      <CommonModal
        isOpen={showErrorModal}
        onClose={closeErrorMessageModal}
        title="Oops!"
        size="medium"
        bodyContent={
          <p>
            Something went wrong while processing your request. Please try again
            later. We apologize for the inconvenience and appreciate your
            patience!
          </p>
        }
      />
    </>
  );
};

export default ListingPage;
